<template>
  <div
    class="card side-filter-card mb-3 p-custom-arrow p-custom-arrow-sidefilter"
  >
    <div class="card-body">
      <div class="row">
        <!-- Case Notes -->
        <div class="col-md-3">
           <!-- Search & Clear -->
          <div class="form-group mb-0">
            <button
              type="button"
              title="Search"
              class="btn p-0 text-body"
              @click="onSearch"
            >
              <span class="material-symbols-outlined fs-22 p-1 pl-0" style="color:#20478d">search</span>
            </button>

            <button
              type="button"
              title="Clear Filters"
              class="btn p-0 text-body"
              @click="clearFilters"
            >
              <span class="material-symbols-outlined fs-22 p-1 pl-0" style="color:#20478d"
                >filter_alt_off</span
              >
            </button>
          </div>
          <div class="form-group">
            <b-form-input
              v-model="filters.casenotes"
              placeholder="Search"
              aria-label="Case Notes"
              aria-labelledby="Case Notes"
              type="text"
              name="casenotes"
              size="sm"
            />
          </div>
        </div>

        <!-- Region -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Geographic Region</label>
            <p-dropdown
              v-model="filters.region"
              :options="regions"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Region"
              aria-labelledby="Region"
              @change="onRegionChange"
            />
          </div>
        </div>

        <!-- Country -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Country</label>
            <p-dropdown
              v-model="filters.country"
              :options="countryList"
              optionLabel="country"
              optionValue="country"
              placeholder="All"
              :disabled="!filters.region"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Country"
              aria-labelledby="Country"
            />
          </div>
        </div>
        <!-- Age -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Age (years)</label>
            <p-dropdown
              v-model="filters.age"
              :options="ageRangeList"
              optionLabel="value"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Age"
              aria-labelledby="Age"
            />
          </div>
        </div>

        <!-- Gender -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Gender</label>
            <p-dropdown
              v-model="filters.gender"
              :options="genderList"
              optionLabel="value"
              optionValue="value"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Gender"
              aria-labelledby="Gender"
            />
          </div>
        </div>

        <!-- Familial -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Familial Disease</label>
            <p-dropdown
              v-model="filters.familialDisease"
              :options="familialDisease"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Familial Disease"
              aria-labelledby="Familial Disease"
            />
          </div>
        </div>

        <!-- Environmental -->
        <div class="col-md-3"  v-if="hasSelectedTumorSiteFeature(tumorSiteFeatures.ENVIRONMENTAL_SETTINGS)">
          <div class="form-group">
            <label>Environmental Setting</label>
            <p-dropdown
              v-model="filters.environmentalSettings"
              :options="environmentalSettings"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Environmental Setting"
              aria-labelledby="Environmental Setting"
            />
          </div>
        </div>

        <div class="col-md-3" v-if="hasSelectedTumorSiteFeature(tumorSiteFeatures.BIOMARKER_PROFILE)">
          <div class="form-group">
            <label>Biomarker Profile</label>
            <p-dropdown
              v-model="filters.biomarkerProfile"
              :options="biomarkerProfilesList"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Biomarker Profile"
              aria-labelledby="Biomarker Profile"
            />
          </div>
        </div>

        <div class="col-md-3" v-if="hasSelectedTumorSiteFeature(tumorSiteFeatures.NEOADJUVANT_TREATMENT)">
          <div class="form-group">
            <label>Neoadjuvant Treatment</label>
            <p-dropdown
              v-model="filters.neoadjuvantTreatment"
              :options="neoadjuvantTreatmentsList"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Neoadjuvant Treatment"
              aria-labelledby="Neoadjuvant Treatment"
            />
          </div>
        </div>

        <div class="col-md-3" v-if="hasSelectedTumorSiteFeature(tumorSiteFeatures.RISK_FACTORS)">
          <div class="form-group">
            <label>Risk Factors</label>
            <p-dropdown
              v-model="filters.riskFactor"
              :options="filteredRiskFactors"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Risk Factors"
              aria-labelledby="Risk Factors"
            />
          </div>
        </div>

        <!-- Specific Cohort -->
        <div class="col-md-3" v-if="hasSelectedTumorSiteFeature(tumorSiteFeatures.SPECIFIC_COHORT)">
          <div class="form-group">
            <label>Specific Cohort</label>
            <p-dropdown
              v-model="filters.specificCohortId"
              :options="cohorts"
              optionLabel="name"
              optionValue="id"
              placeholder="All"
              class="p-dropdown-custom form-control form-control-sm"
              aria-label="Specific Cohort"
              aria-labelledby="Specific Cohort"
            />
          </div>
        </div>

        <!-- Tumor Type -->
        <div class="col-md-3">
          <div class="form-group cus-placeholder">
            <label>Tumor Type</label>
            <wtr-cascade-selection
                :placeholder="'Select Tumor Type'"
                :options="tumorTypesTransformed"
                optionLabel="name"
                compairKey="id"
                levelKey="parentId"
                :addOptionFlag="'freeTextOption'"
                :removeOptionFlag="'isDefault'"
                :selectedFields="(selectedTumorType != null) ? [selectedTumorType] : []"
                :selectionFirstLevel="'single'"
                :selectionSecondLevel="'single'"
                :addFieldTumorSiteId="Number(selectedtumorSiteId)"
                :hideFlag="true"
                :dropdownName="'tumorType'"
                class="viewer-tumor-type-dropdown"
                @selected-items="
                  (payload) => {
                    selectedTumorType = payload[0];
                    onTumorTypeChange(false);
                  }
                "
              ></wtr-cascade-selection>
          </div>
        </div>

        <!-- Tumor Subtype -->
        <div class="col-md-3">
          <div class="form-group">
            <label>Tumor Subtype</label>
            <p-dropdown
              id="tumorSubType"
              :aria-label="$t('common.tumorSubType')"
              :aria-labelledby="$t('common.tumorSubType')"
              :placeholder="$t('common.tumorSubType')"
              :title="`Select Tumor Subtype`"
              class="p-dropdown-custom form-control form-control-sm w-100"
              optionLabel="name"
              optionValue="id"
              v-model="filters.tumorSubType"
              :options="[{ id: '', name: 'All' }, ...tumorSubTypes]"
            >
            </p-dropdown>
          </div>
        </div>

     

        <!-- Add Manual -->
        <div class="col-md-3" style="margin-top: 32px">
          <div class="form-group">
            <button
              type="button"
              class="btn btn-primary"
              @click="$emit('addmanual')"
            >
              Add selected cases to {{ type === 'Collection' ? 'collection' : 'assignment' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Country from "../../../models/model-countries.js";
import ageList from "../../../models/model-age-range.js";
import gendersList from "../../../models/model-gender.js";
import wtrCascadeSelection from "@/components/common/wtr-cascade-selection.vue";
import { orderBy, cloneDeep } from "lodash";
import { TUMOR_SITE_FEATURES, hasTumorSiteFeature } from "../../../constants/constants-tumor-site";
export default {
  name: "manual-case-filter",
  props: {
    totalCases: { type: Number, default: 0 },
    type:String,
    selectedtumorSiteId: Number
  },
  components: {
    "wtr-cascade-selection": wtrCascadeSelection
  },
  data() {
    return {
      filters: {
        casenotes: "",
        region: "",
        country: "",
        age: [],
        gender: "",
        familialDisease: "",
        environmentalSettings: "",
        specificCohortId: "",
        tumorType: "",
        tumorSubType: "",
        biomarkerProfile: "",
        neoadjuvantTreatment: "",
        riskFactors: "",
      },
      regions: [],
      countryList: [],
      ageRangeList: cloneDeep(ageList),
      genderList: cloneDeep(gendersList),
      familialDisease: [],
      environmentalSettings: [],
      cohorts: [],
      tumorTypes: [],
      tumorSubTypes: [],
      previewMode: true,
      riskFactorsList: [],
      filteredRiskFactors: [],
      biomarkerProfilesList: [],
      neoadjuvantTreatmentsList: [],
      tumorTypesTransformed: [],
      tumorTypeCategoriesList: [],
      selectedTumorType:null,
      tumorSiteFeatures: TUMOR_SITE_FEATURES,
    };
  },
  methods: {
    hasSelectedTumorSiteFeature(feature) {
      return hasTumorSiteFeature(this.selectedtumorSiteId, feature);
    },
    async fetchRegions() {
      const res = await this.$api.getAllGeographicregionsFact();
      this.regions = res.data.payload || [];
    },
    async fetchCohorts() {
      const res = await this.$api.getAllSpecificCohortsFact();
      const excluded = ["Iodine-deficiency", "NIFTP study", "HTT study"];
      this.cohorts = orderBy(
        res.data.payload.filter((item) => !excluded.includes(item.name)),
        ["name"],
        ["asc"]
      );
    },
    async fetchFamilial(tumorSiteId) {
      const res = await this.$api.getAllFamilialDiseaseFact(tumorSiteId);
      this.familialDisease = res.data.payload.filter((t) => t.parentId == null);
    },
    async fetchEnvironment() {
      const res = await this.$api.getAllEnvironmentalSettingFact(this.selectedtumorSiteId);
      this.environmentalSettings = res.data.payload.filter(
        (t) => t.parentId == null
      );
    },
    async fetchRiskFactors() {
      const res = await this.$api.getAllRiskFactors(this.selectedtumorSiteId);
      this.riskFactorsList = res.data.payload
      this.filterAndSortRiskFactors();
    },

    async getAllBiomarkerProfiles() {
      const res = await this.$api.getAllBiomarkerProfiles(this.selectedtumorSiteId);
      this.biomarkerProfilesList = res.data.payload
      // this.filterAndSortBiomarkerProfiles();
    },

    async getAllNeoadjuvantTreatment() {
      const res = await this.$api.getAllNeoadjuvantTreatment(this.selectedtumorSiteId);
      this.neoadjuvantTreatmentsList = res.data.payload
      // this.filterAndSortBiomarkerProfiles();
    },

    async fetchTumorTypes(tumorSiteId) {
      const [tumorTypesResponse, tumorCategoriesResponse] = await Promise.all([
        this.$api.getAllTumortypesFact(tumorSiteId),
        this.$api.getAllTumortypeCategoriesFact(tumorSiteId),
      ]);

      this.tumorTypeCategoriesList =
        tumorCategoriesResponse.data.payload || [];

      const transformedData = this.transformTumorTypes(
        tumorTypesResponse.data.payload,
      );
      this.tumorTypesTransformed = transformedData;
      // this.tumorTypes = this.groupedTumorTypes(types, tumorSiteId);
      // console.log("types", this.tumorTypes, tumorSiteId)
    },
    getCategoryName(categoryId) {
      const category = this.tumorTypeCategoriesList.find(
        (item) => Number(item.id) === Number(categoryId)
      );

      return category ? category.name : `Category ${categoryId}`;
    },

     transformTumorTypes(tumorTypes) {
      // Preserve the category order returned by getAllTumortypeCategoriesFact.
      const groups = {};

      tumorTypes.forEach((tumor) => {
        const categoryId = tumor.tumorTypeCategoryId;
        if (!groups[categoryId]) {
          groups[categoryId] = {
            id: categoryId,
            name: this.getCategoryName(categoryId),
            diagnosticFeatures: [],
          };
        }

        // Convert tumor type to diagnosticFeatures format
        groups[categoryId].diagnosticFeatures.push({
          id: tumor.id,
          name: tumor.name,
          tumorSiteId: tumor.tumorSiteId,
          parentId: categoryId,
          freeTextOption: false,
          onlyNumeric: false,
          onFlyEntry: false,
          onFlyEntryCreatedBy: 0,
          isDefault: false,
          diagnosticFeaturesCategoryId: categoryId,
          orderNo: null,
          otherfeature: false,
          rareGrowthPattern: false,
        });
      });

      const orderedGroups = this.tumorTypeCategoriesList
        .map((category) => groups[category.id])
        .filter(Boolean);

      const remainingGroups = Object.values(groups).filter(
        (group) =>
          !this.tumorTypeCategoriesList.some(
            (category) => Number(category.id) === Number(group.id)
          )
      );

      return [...orderedGroups, ...remainingGroups];
    },
    sortedItemsAsc(items, key) {
      if (items) {
        items = JSON.parse(JSON.stringify(items));

        const unknownIndex = items.findIndex(
          (item) => item[key].toLowerCase() === "unknown",
        );
        if (unknownIndex !== -1) {
          const unknownCountry = items.splice(unknownIndex, 1)[0];
          items.sort((a, b) =>
            a[key].toLowerCase().localeCompare(b[key].toLowerCase()),
          );
          items.push(unknownCountry);
        } else {
          items.sort((a, b) =>
            a[key].toLowerCase().localeCompare(b[key].toLowerCase()),
          );
        }
        return items;
      }
    },

    async fetchTumorSubTypes(typeId) {
      const res = await this.$api.getAllTumorSubTypesFact(typeId);
      this.tumorSubTypes = orderBy(res.data.payload, ["name"], ["asc"]);
    },
    onRegionChange() {
      this.countryList = Country.filter(
        (c) => c.regionId === Number(this.filters.region)
      );
      this.filters.country = "";
    },
    onTumorTypeChange() {
      if (this.selectedTumorType) {
        this.fetchTumorSubTypes(this.selectedTumorType.id);
      } else {
        this.tumorSubTypes = [];
      }
      this.filters.tumorSubType = "";
    },
    convertAgeToRange(ageId) {
      if (!ageId) return null;

      // Convert ageId to number for comparison
      const numericAgeId = Number(ageId);
      const ageItem = this.ageRangeList.find(
        (item) => item.id === numericAgeId
      );

      if (!ageItem) return null;

      const ageString = ageItem.value;
      const updatedAge = [0, 0];

      // Extract all numbers from the age string
      const numbers = ageString.match(/\d+/g)?.map(Number) || [];

      if (numericAgeId === 1) {
        // "Under 1"
        updatedAge[0] = 0;
        updatedAge[1] = 0; // Under 1 year old
      } else if (numericAgeId === 5) {
        // "Over 65"
        updatedAge[0] = 66; // Over 65 means 66 and above
        updatedAge[1] = 120; // Reasonable maximum age
      } else {
        // For ranges like "1 to 5", "6 to 19", "21 to 65"
        if (numbers.length >= 2) {
          updatedAge[0] = numbers[0];
          updatedAge[1] = numbers[1];
        } else if (numbers.length === 1) {
          // Fallback if only one number is found
          updatedAge[0] = numbers[0];
          updatedAge[1] = numbers[0];
        }
      }

      return updatedAge;
    },
    filterAndSortRiskFactors() {
      // Define the order for sorting
      const order = ["Radiation Exposure", "Hormone Therapy", "No"];
      
      // Filter the risk factors
      const filtered = this.riskFactorsList.filter((item) => {
        if (
          item.name === "Radiation Exposure" ||
          item.name === "Hormone Therapy" || item.name === "No"
        )
        return true;
      });

      // Sort the filtered array based on the defined order
      const sortedFiltered = filtered.sort((a, b) => {
        return order.indexOf(a.name) - order.indexOf(b.name);
      });

      this.filteredRiskFactors = sortedFiltered;
    },
    onSearch() {
      const searchFilters = { ...this.filters, gridType: this.previewMode };

      // Convert age ID to range format
      searchFilters.age = this.convertAgeToRange(searchFilters.age);
      // For environmentalSettings, we need to pass the object, not the ID
      if (searchFilters.environmentalSettings) {
        searchFilters.environmentalSettings =
          this.environmentalSettings.find(
            (env) => env.id === searchFilters.environmentalSettings
          ) || null;
      } else {
        searchFilters.environmentalSettings = null;
      }
      // For familialDisease, we need to pass the object, not the ID
      if (searchFilters.familialDisease) {
        searchFilters.familialDisease =
          this.familialDisease.find(
            (fam) => fam.id === searchFilters.familialDisease
          ) || null;
      } else {
        searchFilters.familialDisease = null;
      }

      if (searchFilters.riskFactor) {
        searchFilters.riskFactor =
          this.riskFactorsList.find(
            (rf) => rf.id === searchFilters.riskFactor
          ) || null;
      } else {
        searchFilters.riskFactor = null;
      }

      if (this.selectedTumorType) {
        searchFilters.tumorType = this.selectedTumorType.id;
      }
      console.log(searchFilters)
      
      this.$emit("search", { ...searchFilters, gridType: this.previewMode });
    },
    clearFilters() {
      Object.keys(this.filters).forEach((k) => (this.filters[k] = ""));
      this.countryList = [];
      this.tumorSubTypes = [];
      this.selectedTumorType = null;
      this.$emit("clear");
    },
    changeGridMode(type) {
      this.previewMode = type;
      this.$emit("gridMode", type);
    },
  },
  mounted() {
    if (this.hasSelectedTumorSiteFeature(this.tumorSiteFeatures.BIOMARKER_PROFILE)) {
      this.getAllBiomarkerProfiles();
    }
    if (this.hasSelectedTumorSiteFeature(this.tumorSiteFeatures.NEOADJUVANT_TREATMENT)) {
      this.getAllNeoadjuvantTreatment()
    }
    if (this.hasSelectedTumorSiteFeature(this.tumorSiteFeatures.RISK_FACTORS)) {
      this.fetchRiskFactors();
    }
    if (this.hasSelectedTumorSiteFeature(this.tumorSiteFeatures.ENVIRONMENTAL_SETTINGS)) {
      this.fetchEnvironment();
    }
    if (this.hasSelectedTumorSiteFeature(this.tumorSiteFeatures.SPECIFIC_COHORT)) {
      this.fetchCohorts();
    }
    this.fetchRegions();
    this.fetchFamilial(this.selectedtumorSiteId);
    this.fetchTumorTypes(this.selectedtumorSiteId);
  },
};
</script>
<style>
.cus-placeholder #tumorType {
  height: calc(1.9em + 0.438rem + 0px);
  padding: 0.15rem 0.5rem;
}
</style>
